import React, { useState } from "react";
import { Box, Divider, IconButton, Link, Typography } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

/**
 * A small, dependency-free Markdown renderer. We render to real React nodes
 * (never dangerouslySetInnerHTML) so there's no XSS surface and no extra
 * library — matching the codebase's no-extra-deps approach. Supports headings,
 * code fences (with a Copy button), inline code/bold/italic/links, bullet &
 * numbered lists, task checklists, blockquotes, tables, images and rules.
 */

/** Inline formatting: `code`, **bold**, *italic*, [text](url), images. */
const renderInline = (text: string, keyBase: string): React.ReactNode[] => {
  const nodes: React.ReactNode[] = [];
  // Order matters: images before links (both use the bracket syntax).
  const pattern = /(!\[[^\]]*\]\([^)]+\)|\[[^\]]+\]\([^)]+\)|`[^`]+`|\*\*[^*]+\*\*|\*[^*]+\*)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = pattern.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const tok = m[0];
    const key = `${keyBase}-${i++}`;
    if (tok.startsWith("![")) {
      const im = /!\[([^\]]*)\]\(([^)]+)\)/.exec(tok);
      if (im) nodes.push(<Box key={key} component="img" src={im[2]} alt={im[1]} sx={{ maxWidth: "100%", borderRadius: 1, my: 1, display: "block" }} />);
    } else if (tok.startsWith("[")) {
      const lm = /\[([^\]]+)\]\(([^)]+)\)/.exec(tok);
      if (lm) nodes.push(<Link key={key} href={lm[2]} target="_blank" rel="noopener noreferrer">{lm[1]}</Link>);
    } else if (tok.startsWith("`")) {
      nodes.push(<Box key={key} component="code" sx={{ px: 0.6, py: 0.1, bgcolor: "action.hover", borderRadius: 0.5, fontFamily: "monospace", fontSize: "0.85em" }}>{tok.slice(1, -1)}</Box>);
    } else if (tok.startsWith("**")) {
      nodes.push(<strong key={key}>{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith("*")) {
      nodes.push(<em key={key}>{tok.slice(1, -1)}</em>);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
};

const CodeBlock: React.FC<{ code: string }> = ({ code }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <Box sx={{ position: "relative", my: 1.5 }}>
      <IconButton size="small" onClick={copy} title="Copy"
        sx={{ position: "absolute", top: 4, right: 4, color: "grey.300", "&:hover": { color: "common.white" } }}>
        {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
      </IconButton>
      <Box component="pre" sx={{
        m: 0, p: 2, pr: 5, bgcolor: "#1e1e2e", color: "#e6e6e6", borderRadius: 1,
        overflowX: "auto", fontFamily: "monospace", fontSize: "0.82rem", lineHeight: 1.5,
      }}>
        <code>{code}</code>
      </Box>
    </Box>
  );
};

const Table: React.FC<{ rows: string[][]; key?: any }> = ({ rows }) => {
  const [head, ...body] = rows;
  return (
    <Box component="table" sx={{ borderCollapse: "collapse", width: "100%", my: 1.5, "& td, & th": { border: "1px solid", borderColor: "divider", px: 1.2, py: 0.8, textAlign: "left", fontSize: "0.875rem" }, "& th": { bgcolor: "action.hover" } }}>
      <thead><tr>{head.map((c, i) => <th key={i}>{renderInline(c, `th-${i}`)}</th>)}</tr></thead>
      <tbody>{body.map((r, ri) => <tr key={ri}>{r.map((c, ci) => <td key={ci}>{renderInline(c, `td-${ri}-${ci}`)}</td>)}</tr>)}</tbody>
    </Box>
  );
};

const Markdown: React.FC<{ content: string }> = ({ content }) => {
  const lines = (content || "").replace(/\r\n/g, "\n").split("\n");
  const blocks: React.ReactNode[] = [];
  let i = 0;
  let k = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block
    if (line.trim().startsWith("```")) {
      const buf: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith("```")) { buf.push(lines[i]); i++; }
      i++; // closing fence
      blocks.push(<CodeBlock key={k++} code={buf.join("\n")} />);
      continue;
    }

    // Blank line
    if (line.trim() === "") { i++; continue; }

    // Horizontal rule
    if (/^\s*---\s*$/.test(line)) { blocks.push(<Divider key={k++} sx={{ my: 2 }} />); i++; continue; }

    // Heading
    const h = /^(#{1,4})\s+(.*)$/.exec(line);
    if (h) {
      const level = h[1].length;
      const variant = (["h5", "h6", "subtitle1", "subtitle2"][level - 1] || "subtitle2") as any;
      blocks.push(
        <Typography key={k++} variant={variant} fontWeight={700} sx={{ mt: level <= 2 ? 2.5 : 1.5, mb: 1 }}>
          {renderInline(h[2], `h-${k}`)}
        </Typography>
      );
      i++;
      continue;
    }

    // Blockquote
    if (/^\s*>\s?/.test(line)) {
      const buf: string[] = [];
      while (i < lines.length && /^\s*>\s?/.test(lines[i])) { buf.push(lines[i].replace(/^\s*>\s?/, "")); i++; }
      blocks.push(
        <Box key={k++} sx={{ borderLeft: "4px solid", borderColor: "primary.light", pl: 2, py: 0.5, my: 1.5, color: "text.secondary", bgcolor: "action.hover", borderRadius: "0 4px 4px 0" }}>
          <Typography variant="body2">{renderInline(buf.join(" "), `bq-${k}`)}</Typography>
        </Box>
      );
      continue;
    }

    // Table (header row followed by a |---| separator)
    if (line.includes("|") && i + 1 < lines.length && /^\s*\|?[\s:|-]+\|?\s*$/.test(lines[i + 1]) && lines[i + 1].includes("-")) {
      const rows: string[][] = [];
      const toCells = (l: string) => l.replace(/^\s*\|/, "").replace(/\|\s*$/, "").split("|").map((c) => c.trim());
      rows.push(toCells(line));
      i += 2; // skip header + separator
      while (i < lines.length && lines[i].includes("|") && lines[i].trim() !== "") { rows.push(toCells(lines[i])); i++; }
      blocks.push(<Table key={k++} rows={rows} />);
      continue;
    }

    // Lists (bullets, task checklists, ordered)
    if (/^\s*([-*]|\d+\.)\s+/.test(line)) {
      const items: React.ReactNode[] = [];
      const ordered = /^\s*\d+\.\s+/.test(line);
      while (i < lines.length && /^\s*([-*]|\d+\.)\s+/.test(lines[i])) {
        const raw = lines[i].replace(/^\s*([-*]|\d+\.)\s+/, "");
        const task = /^\[( |x|X)\]\s+(.*)$/.exec(raw);
        if (task) {
          const checked = task[1].toLowerCase() === "x";
          items.push(
            <Box component="li" key={`li-${k}-${i}`} sx={{ listStyle: "none", ml: -3, display: "flex", alignItems: "flex-start", gap: 1, mb: 0.5 }}>
              <Box sx={{ width: 18, height: 18, mt: "2px", borderRadius: 0.5, border: "2px solid", borderColor: checked ? "success.main" : "text.disabled", bgcolor: checked ? "success.main" : "transparent", color: "common.white", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {checked && <CheckIcon sx={{ fontSize: 14 }} />}
              </Box>
              <Typography variant="body2" sx={{ textDecoration: checked ? "line-through" : "none", color: checked ? "text.secondary" : "text.primary" }}>
                {renderInline(task[2], `task-${i}`)}
              </Typography>
            </Box>
          );
        } else {
          items.push(<Box component="li" key={`li-${k}-${i}`} sx={{ mb: 0.5 }}><Typography variant="body2" component="span">{renderInline(raw, `li-${i}`)}</Typography></Box>);
        }
        i++;
      }
      blocks.push(<Box component={ordered ? "ol" : "ul"} key={k++} sx={{ pl: 3, my: 1 }}>{items}</Box>);
      continue;
    }

    // Paragraph (consume consecutive non-blank, non-special lines)
    const buf: string[] = [];
    while (i < lines.length && lines[i].trim() !== "" && !/^\s*(#{1,4}\s|```|>|[-*]\s|\d+\.\s|---\s*$)/.test(lines[i])) {
      buf.push(lines[i]); i++;
    }
    blocks.push(<Typography key={k++} variant="body2" sx={{ my: 1, lineHeight: 1.7 }}>{renderInline(buf.join(" "), `p-${k}`)}</Typography>);
  }

  return <Box>{blocks}</Box>;
};

export default Markdown;
